import uniqueId from 'lodash-es/uniqueId'
import { Fade, Loader } from '.'
import { startCloseDetailPanel, finishCloseDetailPanel } from '../../actions/app'
import { debounced } from '../../utils/common'
import detailPanelStyles from '../../assets/style/scss/detail-panel.scss'

const mapStateToProps = (state, ownProps) => {
  const {
    title,
    actions,
    showHeader,
    showFooter,
    shouldCloseDetailPanel,
    showCloseButton,
    onCloseAction,
    closeFocusElem,
    regionLabel,
    canStick = false,
    triggerReStick = null,
    breakpointsToStick = [],
    itemId,
  } = ownProps
  const {
    app: { breakpoint },
  } = state
  return {
    breakpoint,
    title,
    actions,
    showHeader,
    showFooter,
    shouldCloseDetailPanel,
    showCloseButton,
    onCloseAction,
    closeFocusElem,
    regionLabel,
    canStick,
    triggerReStick,
    breakpointsToStick,
    itemId,
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators({ startCloseDetailPanel, finishCloseDetailPanel }, dispatch)

const topOffsetEl = '.cb-desktop-navigation'
const bottomOffsetEl = '#cb-atlas-footer-1'

const queryControlList =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([aria-hidden="true"])'

const DetailPanelHeader = ({ title, showCloseButton, toClose }) => (
  <div className={`${detailPanelStyles['detail-panel-header']}`}>
    {showCloseButton ? (
      <button
        type="button"
        className={`${detailPanelStyles.close} btn-link`}
        data-dismiss="detail-panel"
        aria-label="Close"
        onClick={() => toClose()}
      >
        <span aria-hidden="true">×</span>
      </button>
    ) : null}
    {title ? <h3 className={`${detailPanelStyles['detail-panel-title']}`}>{title}</h3> : null}
  </div>
)

const DetailPanelFooter = ({ actions = [], showFooter }) =>
  showFooter ? (
    <div className={`${detailPanelStyles['detail-panel-footer']}`}>
      {actions.map(a => {
        const dismissable = a.dismissable ? { 'data-dismiss': 'detail-panel' } : {}
        return (
          <button
            type="button"
            key={uniqueId()}
            onClick={() => a.onClick && a.onClick()}
            className={`btn btn-sm ${a.primary ? 'btn-primary' : 'btn-secondary'}`}
            {...dismissable}
            disabled={a.isDisabled || a.busy}
            style={{ position: 'relative' }}
          >
            {a.label}
            {a.busy && (
              <Loader size="sm" style={{ position: 'absolute', top: '12px', right: '10px' }} />
            )}
          </button>
        )
      })}
    </div>
  ) : null

class DetailPanel extends Component {
  constructor(props) {
    super(props)
    this.detailPanelRef = createRef()
    this.state = {
      firstFocusable: null,
      lastFocusable: null,
    }
    this.affixPanel = this.affixPanel.bind(this)
    this.debounceAffixedPanel = this.debounceAffixedPanel.bind(this)
  }

  componentDidMount() {
    const { canStick } = this.props

    if (canStick) {
      this.affixPanel(this.detailPanelRef.current, topOffsetEl, bottomOffsetEl)
      window.addEventListener('resize', this.debounceAffixedPanel)
    }
    // Collect list of all tabable elements within the Detail Panel
    const focusable = this.detailPanelRef.current.querySelectorAll(queryControlList)
    this.setState({ firstFocusable: focusable[0], lastFocusable: focusable[focusable.length - 1] })
    // Set focus to containing element
    this.detailPanelRef.current.focus()
  }

  componentDidUpdate(prevProps, prevState) {
    const { shouldCloseDetailPanel, canStick, triggerReStick, itemId, startCloseDetailPanel } =
      this.props

    if (prevProps.itemId !== itemId) {
      this.detailPanelRef.current.focus()
    }
    if (shouldCloseDetailPanel) {
      startCloseDetailPanel()
    } else if (canStick && prevProps.triggerReStick !== triggerReStick) {
      this.affixPanel(this.detailPanelRef.current, topOffsetEl, bottomOffsetEl)
    }
  }

  componentWillUnmount() {
    const { canStick, onCloseAction, closeFocusElem, finishCloseDetailPanel } = this.props

    if (canStick) {
      $(this.detailPanelRef.current).off('affix.bs.affix', '.sticky-panel')
      window.removeEventListener('resize', this.debounceAffixedPanel)
    }

    if (onCloseAction) {
      onCloseAction()
    }

    if (closeFocusElem) {
      closeFocusElem.focus()
    }

    finishCloseDetailPanel()
  }

  handleKeyPress(event) {
    const { firstFocusable, lastFocusable } = this.state

    // console.log(`Key Pressed: ${event.key}, Shift Pressed: ${event.shiftKey} on element: ${document.activeElement}`)

    if (event.key !== 'Tab') return

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        // console.log('Set focus to LAST')
        lastFocusable.focus()
        event.preventDefault()
      }
    } else if (document.activeElement === lastFocusable) {
      // console.log('Set focus to FIRST')
      firstFocusable.focus()
      event.preventDefault()
    }
  }

  debounceAffixedPanel() {
    if (!this._debouncedAffixPanel) {
      this._debouncedAffixPanel = debounced(500, () =>
        this.affixPanel(this.detailPanelRef.current, topOffsetEl, bottomOffsetEl)
      )
    }
    this._debouncedAffixPanel()
  }

  // http://www.bluthemes.com/blog/7/dynamic-bootstrap-affix-fix
  affixPanel(el, topOffsetEl, bottomOffsetEl) {
    const { breakpoint, breakpointsToStick } = this.props
    const documentHeight = $(document).height()
    const $el = $(el)
    const $topOffsetEl = $(topOffsetEl)
    const $bottomOffsetEl = $(bottomOffsetEl)
    const stickyNavHeight = $topOffsetEl.length ? $topOffsetEl.height() : 0
    const bottomOffsetTop = $bottomOffsetEl.length
      ? $bottomOffsetEl.offset().top - parseInt($bottomOffsetEl.css('marginTop'), 10)
      : 0
    const panelWidth = $el.length ? $el.width() : 0
    const panelTopOffset = $el.length ? $el.offset().top : 0
    const panelOuterHeight = $el.length ? $el.outerHeight() : 0
    const panelHeight = $el.length ? $el.height() : 0
    // when should we start affixing? (the amount of pixels to the top from the element)
    const fromTop = panelTopOffset - stickyNavHeight
    // where is the bottom of the element?
    const fromBottom = documentHeight - (panelTopOffset + panelOuterHeight)
    // where should we stop? (the amount of pixels from the top where the bottom element is)
    // also add the outer height mismatch to the height of the element to account for padding and borders
    const stopOn = documentHeight - (bottomOffsetTop + (panelOuterHeight - panelHeight))
    const affixCss = {
      top: stickyNavHeight,
      width: panelWidth,
      height: document.documentElement.clientHeight - stickyNavHeight,
      overflowX: 'hidden',
      overflowY: 'auto',
      position: '',
    }

    if ($el.length) {
      // if the element doesn't need to get sticky, then skip it so it won't mess up your layout
      if (breakpointsToStick.includes(breakpoint.name) && fromBottom - stopOn > 200) {
        // let's put a sticky width on the element and assign it to the top
        $el.css(affixCss)
        // assign the affix to the element
        $el
          .affix({
            offset: {
              // make it stick where the top pixel of the element is
              top: fromTop,
              // make it stop where the top pixel of the bottom element is
              bottom: stopOn,
            },
            // when the affix get's called then make sure the position is the default (fixed) and it's at the top
          })
          .on('affix.bs.affix', function () {
            $(this).css(affixCss)
          })
        // trigger the scroll event so it always activates
        $(window).trigger('scroll')
      } else {
        $el.off('affix.bs.affix')
        $(window).off('.affix')
        $el.css({ top: '', width: '', height: '', overflowY: '', overflowX: '' })
        $el.removeClass('affix affix-top affix-bottom').removeData('bs.affix')
      }
    }
  }

  render() {
    const {
      title,
      children,
      actions,
      showHeader = true,
      showCloseButton = true,
      showFooter = false,
      startCloseDetailPanel,
      regionLabel,
      closeFocusElem,
    } = this.props

    return (
      <Fade>
        <div className="col-lg-6 col-md-5 col-sm-12">
          <div
            className={`${detailPanelStyles['detail-panel']} clearfix sticky-panel`}
            ref={this.detailPanelRef}
            tabIndex="-1"
            role="region"
            aria-label={regionLabel}
            aria-labelledby={closeFocusElem && closeFocusElem.id ? closeFocusElem.id : ''}
            onKeyDown={event => this.handleKeyPress(event)}
          >
            <DetailPanelHeader
              title={title}
              showCloseButton={showCloseButton}
              toClose={startCloseDetailPanel}
            />
            <div className={`${detailPanelStyles['detail-panel-body']}`}>{children}</div>
            <DetailPanelFooter actions={actions} showFooter={showFooter} />
          </div>
        </div>
      </Fade>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DetailPanel)
